import React, { useEffect, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { RocketLaunchRounded } from "@mui/icons-material";
import { useApp, useTheme } from "contexts";
import { EnumTheme } from "utils/enums";

type Props = {
  window?: () => Window;
};

export function ScrollTop(_props: Props) {
  const { theme } = useTheme();
  const { t } = useApp();
  const isDark = theme.id === EnumTheme.Dark;

  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLaunching, setIsLaunching] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        docHeight > 0 ? Math.min(Math.max(scrollTop / docHeight, 0), 1) : 0;
      setScrollProgress(progress);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const handleScrollTopLaunch = () => {
    setIsLaunching(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      setIsLaunching(false);
    }, 1100);
  };

  // Visibility logic: fade out when at top (scrollProgress <= 0.06) or at very bottom (scrollProgress >= 0.94)
  const isGoToTopVisible = scrollProgress > 0.06 && scrollProgress < 0.94;

  return (
    <Box
      sx={{
        position: "fixed",
        right: theme.spacing.lg * 8,
        bottom: theme.spacing.lg * 8,
        zIndex: 1200,
        opacity: isGoToTopVisible ? 1 : 0,
        visibility: isGoToTopVisible ? "visible" : "hidden",
        transform: isGoToTopVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 0.3s ease, transform 0.3s ease, visibility 0.3s",
        pointerEvents: isGoToTopVisible ? "auto" : "none",
      }}
    >
      <Tooltip title={t.atmosphere?.orbital_return} placement="left">
        <Box
          component="button"
          onClick={handleScrollTopLaunch}
          aria-label={t.atmosphere?.orbital_return}
          sx={{
            width: 44,
            height: 44,
            backgroundColor: theme.color.primary.color,
            color: theme.color.primary.text,
            border: `2px solid ${isDark ? "#ffffff" : "#191b22"}`,
            borderRadius: theme.border.square,
            boxShadow: `3px 3px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              transform: "translate(-2px, -2px)",
              boxShadow: `5px 5px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
            },
            "&:active": {
              transform: "translate(1px, 1px)",
              boxShadow: "none",
            },
          }}
        >
          <RocketLaunchRounded
            sx={{
              fontSize: 22,
              transform: isLaunching
                ? "translateY(-18px) rotate(-45deg)"
                : "rotate(-45deg)",
              transition: "transform 0.4s ease",
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
}
